// Curtain theme transition (View Transitions API).
// The incoming theme wipes across the viewport like a curtain with a soft, gradient edge,
// in the same direction the toggle knob travels: left → right when switching to light,
// right → left to dark.
//
// Gracefully degrades: if the API is unavailable or the user prefers reduced
// motion, the theme is applied instantly with no animation.

import { nextTick } from 'vue';

const TRANSITION_MS = 500;

// Width of the soft feathered edge, as a percentage of the viewport width.
const FEATHER = 20;

export const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Run `apply` (the function that actually flips the theme) inside a curtain View
 * Transition. The incoming theme is revealed by a horizontal wipe.
 *
 * @param {() => void} apply - mutates the DOM to switch the theme (synchronous)
 * @param {{ toDark?: boolean }} [options] - direction: dark wipes right→left, light left→right
 */
export const runThemeTransition = (apply, { toDark = false } = {}) => {
  const root = document.documentElement;
  const startViewTransition = document?.startViewTransition?.bind(document);

  if (!startViewTransition || prefersReducedMotion()) {
    apply();

    return;
  }

  // Make the new colors land instantly instead of riding the app's per-element CSS
  // transitions (background/color: var(--transition-*) is on hundreds of elements).
  // Without this the captured "new" snapshot freezes those elements mid-fade, and the
  // live DOM keeps fading after the wipe ends — a staggered flash. With it, the snapshot
  // equals the final frame and the curtain is the only motion. The class is removed once
  // the snapshots are gone, so normal transitions resume right after.
  root.classList.add('theme-switching');

  // Flip the theme, then let Vue flush every theme-driven update (Vuetify's --v-theme-*
  // stylesheet, etc.) BEFORE the API captures the "new" snapshot. That way the snapshot
  // is the final themed frame and nothing snaps into place once the wipe is gone.
  const transition = startViewTransition(async () => {
    apply();
    await nextTick();
  });

  transition.ready
    .then(() => {
      // Reveal the new snapshot with a soft-edged horizontal wipe. Chrome animates
      // mask-image gradients discretely (they snap at 50%), so instead we keep a FIXED
      // gradient mask — wider than the viewport, with a solid region, a FEATHER-wide
      // black→transparent ramp, then another solid region — and slide it via mask-position,
      // which interpolates smoothly. The ramp is the soft moving edge that blends the new
      // theme into the old over a gradient band. Light slides left → right (knob moves
      // right), dark right → left.
      const imgW = 200 + FEATHER; // mask image width, as a % of the viewport
      const solidEnd = ((100 / imgW) * 100).toFixed(3); // first solid region ends here
      const rampEnd = (((100 + FEATHER) / imgW) * 100).toFixed(3); // second solid region starts here

      const grad = toDark
        ? `linear-gradient(to right, transparent 0%, transparent ${solidEnd}%, black ${rampEnd}%, black 100%)`
        : `linear-gradient(to right, black 0%, black ${solidEnd}%, transparent ${rampEnd}%, transparent 100%)`;
      const size = `${imgW}% 100%`;
      const posFrom = toDark ? '0% 50%' : '100% 50%';
      const posTo = toDark ? '100% 50%' : '0% 50%';

      // The WAAPI animation on the pseudo-element keeps the transition alive for its
      // full duration (the default cross-fade is disabled in main.scss). mask-image /
      // -size / -repeat are constant across both keyframes; only mask-position animates.
      root.animate(
        {
          maskImage: [grad, grad],
          maskSize: [size, size],
          maskRepeat: ['no-repeat', 'no-repeat'],
          maskPosition: [posFrom, posTo],
          WebkitMaskImage: [grad, grad],
          WebkitMaskSize: [size, size],
          WebkitMaskRepeat: ['no-repeat', 'no-repeat'],
          WebkitMaskPosition: [posFrom, posTo],
        },
        {
          duration: TRANSITION_MS,
          easing: 'ease-in-out',
          // Hold the fully-revealed state until the snapshot is torn down, so the mask
          // never reverts on the last frame.
          fill: 'forwards',
          // The new snapshot is raised above the old one (see main.scss); the old stays
          // static underneath and the curtain sweeps the new theme over it.
          pseudoElement: '::view-transition-new(root)',
        },
      );
    })
    .catch(() => {
      // ready rejects if the transition is skipped (e.g. rapid re-toggle) — the theme
      // is already applied, nothing to animate.
    });

  transition.finished.finally(() => {
    root.classList.remove('theme-switching');
  });
};
